import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { useSettings } from "@/hooks/useSettings";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Phone, Mail, MapPin, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  message: z.string().trim().min(1, "Message is required").max(2000),
});

const Contact = () => {
  const settings = useSettings();
  const { user } = useAuth();
  const info = settings.contact_info || {};
  const footerContact = settings.footer_config?.contact || {};
  const phone = info.phone || footerContact.phone;
  const email = info.email || footerContact.email;
  const address = info.address || footerContact.address;

  const [name, setName] = useState(user?.user_metadata?.full_name ?? "");
  const [emailField, setEmailField] = useState(user?.email ?? "");
  const [phoneField, setPhoneField] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ name, email: emailField, phone: phoneField, message });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setSending(true);
    const { error } = await supabase.from("messages").insert({
      user_id: user?.id ?? null,
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone || null,
      message: parsed.data.message,
    });
    setSending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Message sent successfully");
    setMessage("");
    setPhoneField("");
  };

  return (
    <section className="container py-10 max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold">Contact us</h1>
          <p className="text-muted-foreground mt-1">
            We'd love to hear from you. Reach out via any of the channels below.
          </p>
        </div>
        {user && (
          <Button asChild variant="outline" size="sm">
            <Link to="/my-messages">
              <MessageSquare className="h-4 w-4" /> My messages
            </Link>
          </Button>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2 mt-6">
        <div className="space-y-4">
          {phone && (
            <Card className="p-5 flex items-start gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-lg bg-primary/10 text-primary">
                <Phone className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Phone</p>
                <a href={`tel:${phone}`} className="font-medium hover:text-primary">
                  {phone}
                </a>
              </div>
            </Card>
          )}
          {email && (
            <Card className="p-5 flex items-start gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-lg bg-primary/10 text-primary">
                <Mail className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Email</p>
                <a href={`mailto:${email}`} className="font-medium hover:text-primary break-all">
                  {email}
                </a>
              </div>
            </Card>
          )}
          {address && (
            <Card className="p-5 flex items-start gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-lg bg-primary/10 text-primary">
                <MapPin className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Address</p>
                <p className="font-medium">{address}</p>
              </div>
            </Card>
          )}
        </div>

        <Card className="p-6">
          <h2 className="font-display text-xl font-semibold mb-4">Send a message</h2>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-name">Name</Label>
              <Input id="c-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" value={emailField} onChange={(e) => setEmailField(e.target.value)} maxLength={255} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-phone">Phone (optional)</Label>
              <Input id="c-phone" value={phoneField} onChange={(e) => setPhoneField(e.target.value)} maxLength={30} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-msg">Message</Label>
              <Textarea id="c-msg" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} />
            </div>
            <Button type="submit" variant="hero" className="w-full" disabled={sending}>
              {sending ? "Sending…" : "Send message"}
            </Button>
            {!user && (
              <p className="text-xs text-muted-foreground text-center">
                <Link to="/auth" className="underline">Sign in</Link> to view replies in your account.
              </p>
            )}
          </form>
        </Card>
      </div>
    </section>
  );
};

export default Contact;
